"use client";

import { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { projectsApi, userApi } from '../../services/api';
import { Footer } from '../layout/Footer';

export default function DashboardSidebar({ user, selectedProjectId, selectedRepositoryId, onRepositorySelect, collapsed, onCollapse }) {
  const [projects, setProjects] = useState([]);
  const [repositories, setRepositories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingRepos, setIsLoadingRepos] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newProject, setNewProject] = useState({ name: "", description: "" });
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [isLoadingUser, setIsLoadingUser] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const intervalRef = useRef(null);
  const prevReposRef = useRef(null);
  const isInitialLoadRef = useRef(true);

  useEffect(() => {
    if (user && user.token) {
      fetchRepos();
      fetchUserDetails();

      // Set up polling for repositories every 10 seconds
      intervalRef.current = setInterval(() => {
        if (user && user.token) {
          fetchUserRepositories(false);
        }
      }, 10000);
    }

    // Cleanup interval on unmount
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [user]);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const fetchUserDetails = async () => {
    setIsLoadingUser(true);
    try {
      const response = await userApi.getUserDetails(user.id, user.token);
      setUserDetails(response.data);
      // Only fetch repositories after user details are loaded
      console.log("User details loaded:", response.data);
      fetchUserRepositories();
    } catch (err) {
      console.error("Error fetching user details:", err);
    } finally {
      setIsLoadingUser(false);
    }
  };

  const fetchUserRepositories = async (isInitial = true) => {
    if (isInitial) {
      setIsLoadingRepos(true);
    }
    
    try {
      const response = await userApi.getUserRepositories(user.id, user.token);
      if (response.data && response.data.repositories) {
        const newRepos = response.data.repositories;
        
        // Compare with previous repositories
        const prevRepos = prevReposRef.current;
        const hasChanges = !prevRepos || JSON.stringify(newRepos) !== JSON.stringify(prevRepos);
        
        if (hasChanges) {
          prevReposRef.current = newRepos;
          setRepositories(newRepos);
        }
      } else if (isInitial) {
        setRepositories([]);
        prevReposRef.current = [];
      }
    } catch (err) {
      console.error("Error fetching repositories:", err);
      if (isInitial) {
        setRepositories([]);
        prevReposRef.current = [];
      }
    } finally {
      if (isInitial) {
        setIsLoadingRepos(false);
      }
    }
  };

  const fetchRepos = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await projectsApi.getAllProjects(user.token);
      setProjects(response.data.projects || []);
    } catch (err) {
      console.error("Error fetching repos:", err);
      setError("Failed to load repos");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateProject = async (e) => {
    e.preventDefault();

    if (!newProject.name.trim()) {
      return;
    }

    setIsCreating(true);

    try {
      await projectsApi.createProject(newProject, user.token);
      setNewProject({ name: "", description: "" });
      setShowCreateForm(false);
      fetchRepos();
    } catch (err) {
      console.error("Error creating project:", err);
      setError("Failed to create project");
    } finally {
      setIsCreating(false);
    }
  };

  if (isMobile) {
    return (
      <div className="fixed inset-0 bg-white dark:bg-gray-900 flex flex-col">
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="text-center space-y-4">
            <h2 className="text-xl font-semibold text-[#24292F] dark:text-white">Desktop Only</h2>
            <p className="text-[#57606A] dark:text-gray-400">
              Please use a desktop browser to access the dashboard
            </p>
            <div className="flex justify-center">
              <button
                onClick={() => window.location.href = '/'}
                className="flex items-center justify-center space-x-2 px-4 py-2 bg-[#24292F] hover:bg-[#24292F]/90 text-white rounded-lg transition-colors text-sm font-medium"
              >
                <span>Back to Home</span>
              </button>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className={`${collapsed ? 'w-20' : 'w-72'
      } flex flex-col bg-white dark:bg-gray-900 border-r border-[#D0D7DE]/30 dark:border-gray-800 relative transition-all duration-300 ease-in-out`}>
      {/* User Profile Section */}
      <div className="p-4 border-b border-[#D0D7DE]/30 dark:border-gray-800 overflow-hidden">
        {!collapsed && (
          <div className="flex items-center space-x-3 animate-fadeIn">
            <div className="w-10 h-10 rounded-full overflow-hidden bg-gray-200 dark:bg-gray-700 flex-shrink-0">
              {userDetails?.picture ? (
                <img
                  src={userDetails.picture}
                  alt={userDetails.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-500 dark:text-gray-400">
                  👤
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-medium text-[#24292F] dark:text-white truncate">
                {userDetails?.name || 'Loading...'}
              </h3>
              <p className="text-xs text-[#57606A] dark:text-gray-400 truncate">
                {userDetails?.email || ''}
              </p>
            </div>
          </div>
        )}
        {collapsed && (
          <div className="flex justify-center animate-fadeIn">
            <div className="w-10 h-10 rounded-full overflow-hidden bg-gray-200 dark:bg-gray-700">
              {userDetails?.picture ? (
                <img
                  src={userDetails.picture}
                  alt={userDetails.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-500 dark:text-gray-400">
                  👤
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Code Platform Bindings */}
      <div className={`px-4 py-3 border-b border-[#D0D7DE]/30 dark:border-gray-800 overflow-hidden transition-opacity duration-300 ${collapsed ? 'opacity-0' : 'opacity-100'}`}>
        <div className={`${collapsed ? 'hidden' : 'block'}`}>
          <h4 className="text-xs font-medium text-[#57606A] dark:text-gray-400 mb-2">
            Connected Platforms
          </h4>
          {userDetails?.code_platform_bindings?.length > 0 ? (
            <div className="space-y-4">
              {userDetails.code_platform_bindings.map((binding, index) => (
                <div key={index} className="space-y-2">
                  {/* Platform Header */}
                  <div className="flex items-center space-x-2">
                    <div className="w-6 h-6 flex items-center justify-center">
                      {binding.platform === 'github' ? (
                        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.237 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
                        </svg>
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-xs text-gray-500 dark:text-gray-400">
                          🔗
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-1">
                        <span className="text-sm font-medium text-[#24292F] dark:text-white truncate">
                          {binding.platform_user_name}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* GitHub Repositories for this user */}
                  {binding.platform === 'github' && (
                    <div className="pl-2 mb-4">
                      {isLoadingRepos ? (
                        <div className="flex justify-center py-2">
                          <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-[#2188FF]"></div>
                        </div>
                      ) :
                        <nav>
                          <ul className="space-y-1">
                            {repositories && Array.isArray(repositories) && repositories
                              .map((repo) => (
                                <li key={repo.id}>
                                  <button
                                    onClick={() => onRepositorySelect(repo.id)}
                                    className={`flex items-center p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 text-[#24292F] dark:text-white transition-colors w-full text-left ${selectedRepositoryId === repo.id
                                      ? 'bg-[#0969DA]/10 text-[#0969DA] dark:bg-blue-900/30 dark:text-blue-200'
                                      : ''
                                      }`}
                                  >
                                    <div className="w-4 h-4 mr-1.5 flex-shrink-0 text-[#d81e06]">
                                      <svg className="w-full h-full" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M1004.72704 466.4L557.62304 19.328a65.952 65.952 0 0 0-93.28 0L360.47104 123.2l78.176 78.176a96 96 0 0 1 128 128l127.968 127.968a96 96 0 1 1-45.248 45.248l-127.968-127.968a88.832 88.832 0 0 1-9.376 3.872v266.976a96 96 0 1 1-64 0v-266.976a96 96 0 0 1-54.624-131.904L315.22304 168.416 19.31904 464.288a65.952 65.952 0 0 0 0 93.28l447.136 447.072a65.952 65.952 0 0 0 93.28 0l444.992-444.992a65.952 65.952 0 0 0 0-93.28z" fill="currentColor" />
                                      </svg>
                                    </div>
                                    <div className="flex-1 text-xs truncate flex items-center">
                                      <span>{repo.full_name}</span>
                                      {repo.is_private && (
                                        <span className="ml-1 px-1 py-0.5 text-xxs bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded">
                                          private
                                        </span>
                                      )}
                                    </div>
                                  </button>
                                </li>
                              ))}
                          </ul>
                        </nav>
                      }
                    </div>
                  )}

                  {/* Projects under this platform */}
                  <div className="pl-2 space-y-1">
                    {isLoading ? (
                      <div className="flex justify-center py-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-[#2188FF]"></div>
                      </div>
                    ) : (
                      <nav>
                        <ul className="space-y-1">
                          {projects.length === 0 ? (
                            <li className="px-3 py-2 text-sm text-[#57606A] dark:text-gray-400 flex flex-col space-y-2">
                              <div className="flex flex-col space-y-2">
                                {/* 检查是否有GitHub平台绑定 */}
                                {userDetails?.code_platform_bindings?.some(binding => binding.platform === 'github') ? (
                                  <div className="space-y-2">
                                    <button
                                      onClick={async () => {
                                        window.location.href = `https://github.com/apps/first-commit-dev`;
                                      }}
                                      className="flex items-center justify-center space-x-2 px-3 py-1.5 bg-[#24292F] hover:bg-[#24292F]/90 text-white rounded-lg transition-colors text-sm font-medium w-full"
                                      title="Import Repositories"
                                    >
                                      {/* <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.237 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                                      </svg> */}
                                      <span>+ Import CodeBases</span>
                                    </button>
                                    <p className="text-xs text-[#57606A] dark:text-gray-400">
                                      Use the same GitHub account to install to your repos, then you will see them here
                                    </p>
                                  </div>
                                ) : (
                                  <button
                                    onClick={async () => {
                                      const clientId = "Ov23lipG7LrD3ojgSotY";
                                      const redirectUri = `${window.location.origin}/auth/github/callback`;
                                      const githubAuthUrl = `https://github.com/login/oauth/authorize?client_id=${clientId}&redirect_uri=${redirectUri}&scope=read:user%20user:email&state=bind`;
                                      window.location.href = githubAuthUrl;
                                    }}
                                    className="flex items-center justify-center space-x-2 px-3 py-1.5 bg-[#24292F] hover:bg-[#24292F]/90 text-white rounded-lg transition-colors text-sm font-medium"
                                    title="Connect GitHub"
                                  >
                                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                                      <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.237 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
                                    </svg>
                                    <span>Connect GitHub</span>
                                  </button>
                                )}
                              </div>
                            </li>
                          ) : (
                            <>
                              {projects.map((project) => (
                                <li key={project.id}>
                                  <Link
                                    href={`/dashboard/projects/${project.id}`}
                                    className={`flex items-center p-2 rounded-lg transition-all ${selectedProjectId === project.id
                                      ? 'bg-[#0969DA]/10 text-[#0969DA] dark:bg-blue-900/30 dark:text-blue-200'
                                      : 'hover:bg-[#F6F8FA] dark:hover:bg-gray-800 text-[#24292F] dark:text-white'
                                      }`}
                                  >
                                    <span className="w-6 h-6 flex items-center justify-center mr-2 text-[#d81e06]">
                                      <svg className="w-4 h-4" viewBox="0 0 1024 1024" version="1.1" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M1004.72704 466.4L557.62304 19.328a65.952 65.952 0 0 0-93.28 0L360.47104 123.2l78.176 78.176a96 96 0 0 1 128 128l127.968 127.968a96 96 0 1 1-45.248 45.248l-127.968-127.968a88.832 88.832 0 0 1-9.376 3.872v266.976a96 96 0 1 1-64 0v-266.976a96 96 0 0 1-54.624-131.904L315.22304 168.416 19.31904 464.288a65.952 65.952 0 0 0 0 93.28l447.136 447.072a65.952 65.952 0 0 0 93.28 0l444.992-444.992a65.952 65.952 0 0 0 0-93.28z" fill="currentColor" />
                                      </svg>
                                    </span>
                                    <span className="truncate text-sm">{project.name}</span>
                                  </Link>
                                </li>
                              ))}
                              <li className="flex justify-center">
                                <button
                                  onClick={() => setShowCreateForm(true)}
                                  className="w-5 h-5 flex items-center justify-center bg-[#24292F] hover:bg-[#24292F]/90 text-white rounded-full transition-colors"
                                >
                                  <span className="text-xs leading-none">+</span>
                                </button>
                              </li>
                            </>
                          )}
                        </ul>
                      </nav>
                    )}
                  </div>

                  {/* New Repo Form */}
                  {showCreateForm && (
                    <div className="mt-2 flex justify-center">
                      <div className="w-[calc(100%-2rem)] p-3 bg-[#F6F8FA] dark:bg-gray-800 rounded-lg border border-[#D0D7DE]/30 dark:border-gray-700">
                        <form onSubmit={handleCreateProject}>
                          <div className="mb-2">
                            <input
                              type="text"
                              placeholder="Repo name"
                              value={newProject.name}
                              onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
                              className="w-full px-3 py-1.5 text-sm border border-[#D0D7DE]/50 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2188FF]/50 bg-white dark:bg-gray-900 dark:text-white"
                              required
                            />
                          </div>
                          <div className="mb-2">
                            <textarea
                              placeholder="Description (optional)"
                              value={newProject.description}
                              onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
                              className="w-full px-3 py-1.5 text-sm border border-[#D0D7DE]/50 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2188FF]/50 bg-white dark:bg-gray-900 dark:text-white"
                              rows="2"
                            ></textarea>
                          </div>
                          <div className="flex space-x-2">
                            <button
                              type="submit"
                              disabled={isCreating || !newProject.name.trim()}
                              className="flex-1 h-8 px-3 bg-[#0969DA] text-white rounded-lg hover:bg-[#0969DA]/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                            >
                              {isCreating ? "Creating..." : "Create"}
                            </button>
                            <button
                              type="button"
                              onClick={() => setShowCreateForm(false)}
                              className="flex-1 h-8 px-3 bg-white hover:bg-[#F6F8FA] text-[#24292F] text-sm rounded-lg transition-all dark:bg-gray-800 dark:hover:bg-gray-700 dark:text-white font-medium border border-[#D0D7DE]/50 dark:border-gray-700"
                            >
                              Cancel
                            </button>
                          </div>
                        </form>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-[#57606A] dark:text-gray-400">
                No platforms connected yet
              </p>
              <button
                onClick={async () => {
                  const clientId = "Ov23lipG7LrD3ojgSotY";
                  const redirectUri = `${window.location.origin}/auth/github/callback`;
                  const githubAuthUrl = `https://github.com/login/oauth/authorize?client_id=${clientId}&redirect_uri=${redirectUri}&scope=read:user%20user:email&state=bind`;
                  window.location.href = githubAuthUrl;
                }}
                className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-[#24292F] hover:bg-[#24292F]/90 text-white rounded-lg transition-colors text-sm font-medium"
                title="Connect GitHub"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.237 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
                </svg>
                <span>Connect GitHub</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Collapse Button - Modern design */}
      <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2">
        <button
          onClick={onCollapse}
          className="w-8 h-8 flex items-center justify-center bg-white dark:bg-gray-800 border border-[#D0D7DE]/30 dark:border-gray-700 rounded-full shadow-sm hover:shadow-md transition-all"
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          <svg
            className={`w-4 h-4 text-[#57606A] dark:text-gray-400 transition-transform duration-300 ${collapsed ? 'rotate-180' : ''}`}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>
      </div>
    </div>
  );
} 